import React, { useCallback, useState } from "react";
import type { Permission } from "../../lib/permissions";
import { ChevronDown, Flag, LogOut, Settings, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { LanguageSwitcher } from "../language-switcher";
import { cn } from "../../lib/utils";
import { VERSION_LABEL, VERSION_TITLE } from "../../lib/version";

export type NavLabelKey = string;

export interface NavItem {
  labelKey: string;
  shortLabelKey?: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  /** What the person must be allowed to do to see the entry (see lib/permissions). */
  permission?: Permission;
  /** A count worth showing on the entry itself (reports waiting, for the operator). */
  badge?: number;
}

const COLLAPSED_KEY = "sideNavCollapsed";

// A per-device convenience: storage failures just mean the sidebar opens full.
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // Not remembered on this device; the toggle still works for this visit.
  }
}

export const isDesktop = () => window.matchMedia("(min-width: 768px)").matches;

/** Whether the desktop sidebar is reduced to icons, remembered per device. */
export function useSideNavCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      writeCollapsed(!prev);
      return !prev;
    });
  }, []);
  return [collapsed, toggle];
}

// Longest matching prefix wins, so "/admin" is not highlighted on "/admin/users".
export function activeItemPath(items: NavItem[], pathname: string): string | null {
  let best: string | null = null;
  for (const { path } of items) {
    if ((pathname === path || pathname.startsWith(path + "/")) && (best === null || path.length > best.length)) {
      best = path;
    }
  }
  return best;
}

interface NavListProps {
  items: NavItem[];
  settingsItems?: NavItem[];
  switchItems?: NavItem[];
  activePath: string;
  onNavigate: (path: string) => void;
  collapsed?: boolean;
  onReport?: () => void;
}

function NavList({ items, settingsItems = [], switchItems = [], activePath, onNavigate, collapsed, onReport }: NavListProps) {
  const { t } = useTranslation();
  const active = activeItemPath([...items, ...settingsItems, ...switchItems], activePath);
  // Closed by default, but never hide the page the user is on.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsActive = settingsItems.some((item) => item.path === active);
  const showSettings = settingsOpen || settingsActive;

  const row = (item: NavItem, idle: string, indent = false) => {
    const label = t(item.labelKey) as string;
    const isActive = item.path === active;
    const badge = item.badge !== undefined && item.badge > 0 ? item.badge : null;
    return (
      <button
        key={item.path}
        type="button"
        onClick={() => onNavigate(item.path)}
        aria-current={isActive ? "page" : undefined}
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        className={cn(
          "flex w-full items-center gap-3 text-sm text-left transition-colors",
          collapsed ? "justify-center px-0 py-3" : cn("py-3.5 md:py-2.5", indent ? "pl-11 pr-4" : "px-4"),
          isActive ? "bg-secondary text-foreground font-medium" : cn(idle, "hover:bg-muted")
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{label}</span>}
        {!collapsed && badge !== null && (
          <span className="ml-auto rounded-full bg-destructive px-1.5 text-xs font-semibold tabular-nums text-destructive-foreground">
            {badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto py-2">
      {items.map((item) => row(item, "text-muted-foreground"))}
      {settingsItems.length > 0 && (
        <>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setSettingsOpen(!showSettings)}
            aria-expanded={showSettings}
            aria-label={collapsed ? (t("nav.settings") as string) : undefined}
            title={collapsed ? (t("nav.settings") as string) : undefined}
            className={cn(
              "flex w-full items-center gap-3 text-sm text-left text-muted-foreground transition-colors hover:bg-muted",
              collapsed ? "justify-center px-0 py-3" : "px-4 py-3.5 md:py-2.5"
            )}
          >
            <Settings className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 truncate">{t("nav.settings")}</span>
                <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform", showSettings && "rotate-180")} />
              </>
            )}
          </button>
          {showSettings && settingsItems.map((item) => row(item, "text-muted-foreground", !collapsed))}
        </>
      )}
      {switchItems.length > 0 && (
        <>
          <div className="my-2 border-t border-border" />
          {switchItems.map((item) => row(item, "text-foreground"))}
        </>
      )}
      {onReport && (
        <>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={onReport}
            aria-label={collapsed ? (t("signalement.action") as string) : undefined}
            title={collapsed ? (t("signalement.action") as string) : undefined}
            className={cn(
              "flex w-full items-center gap-3 text-sm text-left text-destructive transition-colors hover:bg-destructive-soft",
              collapsed ? "justify-center px-0 py-3" : "px-4 py-3.5 md:py-2.5"
            )}
          >
            <Flag className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">{t("signalement.action")}</span>}
          </button>
        </>
      )}
    </div>
  );
}

interface SideMenuProps {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  settingsItems?: NavItem[];
  switchItems?: NavItem[];
  activePath: string;
  onNavigate: (path: string) => void;
  userName: string;
  roleLabel: string;
  onLogout: () => void;
  /** Opens the report dialog; absent when reporting is not allowed. */
  onReport?: () => void;
}

/** Mobile drawer. On desktop the menu is part of the layout ({@link SideNav}). */
export const SideMenu = React.memo(function SideMenu({ open, onClose, items, settingsItems, switchItems, activePath, onNavigate, userName, roleLabel, onLogout, onReport }: SideMenuProps) {
  const { t } = useTranslation();

  if (!open) return null;

  const go = (path: string) => {
    onNavigate(path);
    onClose();
  };
  const report = onReport && (() => {
    onClose();
    onReport();
  });

  return (
    <div className="fixed inset-0 z-50 flex md:hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <nav className="relative z-10 flex h-full pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] w-72 max-w-[85vw] flex-col bg-card border-r border-border">
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{userName}</p>
            <p className="text-xs text-muted-foreground">{roleLabel}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t("nav.closeMenu")}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <NavList items={items} settingsItems={settingsItems} switchItems={switchItems} activePath={activePath} onNavigate={go} onReport={report} />

        <div className="border-t border-border p-4 space-y-3">
          <LanguageSwitcher />
          <Button variant="outline" size="sm" className="w-full justify-center" onClick={onLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            {t("nav.logout")}
          </Button>
          <p className="text-center text-[11px] tabular-nums text-muted-foreground/70" title={VERSION_TITLE}>
            {VERSION_LABEL}
          </p>
        </div>
      </nav>
    </div>
  );
});

interface SideNavProps extends Omit<SideMenuProps, "open" | "onClose"> {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/** Desktop sidebar: always in the layout, either full or reduced to icons. */
export const SideNav = React.memo(function SideNav({ items, settingsItems, switchItems, activePath, onNavigate, userName, roleLabel, onLogout, onReport, collapsed, onToggleCollapsed }: SideNavProps) {
  const { t } = useTranslation();
  const toggleLabel = collapsed ? t("nav.expandMenu") : t("nav.collapseMenu");
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <nav
      aria-label={t("bottomNav.navigationPrincipale")}
      className={cn(
        "hidden md:flex sticky top-[calc(5rem+var(--banner-h,0px))] h-[calc(100dvh-5rem-var(--banner-h,0px))] shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className={cn("flex items-center border-b border-border", collapsed ? "justify-center py-2" : "justify-between gap-2 px-4 py-3")}>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{userName}</p>
            <p className="text-xs text-muted-foreground truncate">{roleLabel}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapsed}
          aria-label={toggleLabel}
          title={toggleLabel}
          aria-expanded={!collapsed}
          className="shrink-0 text-muted-foreground"
        >
          <ToggleIcon className="h-4 w-4" />
        </Button>
      </div>

      <NavList items={items} settingsItems={settingsItems} switchItems={switchItems} activePath={activePath} onNavigate={onNavigate} collapsed={collapsed} onReport={onReport} />

      <div className={cn("border-t border-border", collapsed ? "flex flex-col items-center gap-1 py-2" : "p-4 space-y-3")}>
        {collapsed ? (
          <Button variant="ghost" size="icon" onClick={onLogout} aria-label={t("nav.logout")} title={t("nav.logout")}>
            <LogOut className="h-4 w-4" />
          </Button>
        ) : (
          <>
            <LanguageSwitcher />
            <Button variant="outline" size="sm" className="w-full justify-center" onClick={onLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              {t("nav.logout")}
            </Button>
            <p className="text-center text-[11px] tabular-nums text-muted-foreground/70" title={VERSION_TITLE}>
              {VERSION_LABEL}
            </p>
          </>
        )}
      </div>
    </nav>
  );
});
